import type {
  GQL_GET_RULE_FIRING_DATA_MAPQuery,
  GQL_GET_RULE_FIRING_DATA_MAPQueryVariables,
  GQL_RULE_SEARCHQuery,
  GQL_RULE_TRACE_SEARCHQuery,
  qx_eFlowTraceActionStatus_enum
} from '@/graphql/types';
import { FAIcon, GenericObject } from '@/types';
import { Envs } from '@/utils/EnvProvider';
import { Component } from 'vue';
import {
  FlowTrigger,
  FlowConditionOperator,
  FlowTriggerCondition,
  FlowGuardOperator,
  FlowGuard,
  FlowGroupOperator,
  FlowResultBranch
} from 'qx-flow-orm';

export enum RuleGuardRepeatExpiresType {
  Seconds = '_secondsAgo'
}

export enum FlowTriggerType {
  Event = 'flow_event',
  Scheduled = 'scheduled_at'
}

export type RuleGuardRepeat = {
  match: string[];
  value: number;
  expires: {
    type: RuleGuardRepeatExpiresType;
    value: number;
  };
};

export type RuleGuardOf<T> = Omit<FlowGuard, 'options'> & { options: T };

export type RuleGuardComponentProps<T> = {
  guard: RuleGuardOf<T>;
  type?: string;
  editable?: boolean;
  depth?: number;
};

export type RuleCondition = FlowTriggerCondition & {
  uuid?: string;
};

export type RuleConditionObject = {
  model: string;
  modelID?: string;
  field: string;
  type: string;
  operator: string;
  operators: string[];
  options: unknown[];
  value: unknown | unknown[]; // the raw value in the DB
  displayValue?: unknown | unknown[]; // the formatted value, if a `displayValue` method is available on the `model.field`
};

export type FlowConditionEnumValue = { type: '_enum'; value: string };

export type RuleConditionGroupOperatorValue = FlowGroupOperator | FlowConditionOperator | FlowGuardOperator;

// DEV NOTE :: use this over the one generated in @/graphql/types
export type FlowAction = {
  arguments?: { name: string, type: string }[];
  environments: string;
  name: string;
}

/**
 * Currently used by the UI to determine
 * which component to load based off
 * of the flow action's `type`.
 */
export enum FlowActions {
  NotificationEmail = 'flow_email_notification',
  NotificationSms = 'flow_sms_notification',
  TriggerOutput = 'flow_triggerOutput',
  ResetOutput = 'flow_resetOutput',
  LockDownPlace = 'flow_lockDownPlace',
  ReleaseLockDownPlace = 'flow_releaseLockDownPlace',
  Webhook = 'flow_webhook',
  RevertDoor = 'flow_revertDoor',
  PtzMoveToPreset = 'flow_ptzMoveToPreset',
  LockDoor = 'flow_lockDoor',
  LockDownDoor = 'flow_lockDownDoor',
  ReleaseLockDownDoor = 'flow_releaseLockDownDoor',
  UnlockDoor = 'flow_unlockDoor',
  AccessDoor = 'flow_accessDoor',
  // `flow_armPartition` is not actually returned by getFlowActions — the real partition actions are
  // the per-mode ones below. Kept only for the stale hidden-argument reference; safe to remove later.
  ArmPartition = 'flow_armPartition',
  AwayArmPartition = 'flow_awayArmPartition',
  StayArmPartition = 'flow_stayArmPartition',
  DisarmPartition = 'flow_disarmPartition',
  BypassZone = 'flow_bypassZone',
  PunchIn = 'flow_punchIn',
  PunchOut = 'flow_punchOut',
  Unknown = 'unknown',
}

/**
 * The form state for a Rule Action.
 */
export type RuleAction = {
  // used by actions that are only in UI-state, not yet saved to the DB
  create?: boolean;
  // helps with identifying unique actions in the UI
  uuid?: string;
  type: FlowActions;
  arguments: Record<string, unknown>;
  rateLimit: number | undefined;
  delay?: number | undefined;
};

export type BranchKey = Exclude<keyof FlowResultBranch, 'action'>;

export type RuleIfThenAction = Pick<RuleAction, 'create' | 'uuid'> & {
  _if: { action: RuleAction | null } & Record<BranchKey, RuleFlowAction[]>;
};

export type RuleFlowAction = RuleAction | RuleIfThenAction;

export const isRuleIfThenAction = (action: RuleFlowAction): action is RuleIfThenAction =>
  '_if' in action && !!action._if && 'action' in action._if;

/**
 * Structure of RuleAction Argument Overrides
 */
export type ActionArgumentRender = {
  argument: string;
  label: string;
  description: string;
  isRequired: boolean;
  isType: string;
  isArray: boolean;
  displayComponent: Component;
  formComponent: Component;
  validate: (arg: unknown) => Promise<boolean>;
  props: ((action: RuleAction) => GenericObject) | GenericObject | null;
}

/**
 * The necessary structure for a RuleAction
 * to be properly utilized in the UI.
 */
export type RuleActionOverrideType = {
  // ...merge getFlowAction data via useRuleActions
  name: string;
  environments: Envs[];
  // ...merge UI overrides via useRuleActions
  icon: FAIcon;
  title: string;
  desc: string;
  // ...generated via useRuleActions
  form: RuleAction;
  // ...generated via config function
  arguments: {
    [key: string]: Partial<ActionArgumentRender>;
  };
  validate: (args: unknown) => Promise<boolean>;
  displayComponent: Component;
  formComponent: Component;
};

/**
 * Generated config for a given RuleAction
 * that helps build the UI around it.
 */
export type RuleActionConfig = {
  title: string;
  desc: string | undefined;
  icon: FAIcon;
  validate: (args: unknown) => Promise<boolean>;
  displayComponent: string | Component;
  formComponent: string | Component;
  arguments: ActionArgumentRender[];
  environments?: Envs[] | undefined;
  form?: RuleAction | undefined;
  action: RuleAction;
};

export type TraceSource = {
  type: string;
  name: string;
  avatar?: string;
};

export type TraceSourceContext = 'trigger' | 'cancelled';

export type RuleTraceSources = Record<TraceSourceContext, TraceSource[]>;

export type RuleTraceEvent = { at?: string; [key: string]: unknown };

export type RuleTraceGuardState = 'Scheduled' | 'Released' | 'Passed' | 'Blocked';

export type RuleTraceAction = {
  index: string;
  action: FlowActions;
  time?: string;
  status?: qx_eFlowTraceActionStatus_enum;
  failedMessage?: string;
  ranOnGateway?: boolean;
};

export type RuleTraceBody = {
  actions?: Record<string, RuleTraceAction>;
  conditions?: Array<{ condition?: unknown; passed?: boolean }>;
  guards?: Array<{ guard?: unknown; passed?: boolean; state?: RuleTraceGuardState }>;
  delay?: number;
  cancelledBy?: RuleTraceEvent;
};

export type RuleTrace = Omit<GQL_RULE_TRACE_SEARCHQuery['flowTraces'][number], 'trace'> & {
  trace?: RuleTraceBody;
};

export type RuleTraceTiming = {
  delaySeconds: number;
  dueAt: string | null;
  pending: boolean;
  remainingSeconds: number;
  heldSeconds: number | null;
};

export type RuleTraceTimelineTrace = Pick<RuleTrace, 'state' | 'createdAt' | 'finishedAt' | 'trace'>;

export type RuleTraceTimelineNode = {
  key: string;
  label: string;
  tone: 'info' | 'success' | 'muted' | 'warning';
  time?: string;
  desc?: string;
  dueAt?: string;
  sources?: { context: TraceSourceContext; items: TraceSource[] };
};

export type RuleTraceTimelineContext = {
  trace: RuleTraceTimelineTrace;
  sources: RuleTraceSources;
  delay: string;
  held: string;
  countdown: string;
  dueAt?: string;
};

export type RuleTraceCheck = { passed?: boolean; state?: RuleTraceGuardState };

export type RuleTraceTone = 'warning' | 'primary' | 'danger' | 'success' | 'success-light' | 'muted' | 'neutral';

export type RuleTraceCheckDisplay = {
  icon: string;
  tone: 'success' | 'danger' | 'warning';
  label: string;
};

export type RuleTraceCheckItem = RuleTraceCheckDisplay & { text: string };

export type RuleTraceRecords = Partial<Omit<GQL_GET_RULE_FIRING_DATA_MAPQuery, '__typename'>>;
export type RuleTraceSourceModel = keyof RuleTraceRecords;
export type RuleTraceSourceRecord = NonNullable<RuleTraceRecords[RuleTraceSourceModel]>[number];
export type RuleTraceSourceKey = keyof GQL_GET_RULE_FIRING_DATA_MAPQueryVariables;
export type RuleTraceSourceVariables = Record<RuleTraceSourceKey, string[]>;
export type RuleTraceEvents = Array<RuleTraceEvent | null | undefined>;

export type RuleTraceSourceLookup = {
  model: RuleTraceSourceModel;
  matches: (record: RuleTraceSourceRecord, id: string) => boolean;
};

export type RuleTraceFoundSource = {
  model: RuleTraceSourceModel;
  record: RuleTraceSourceRecord;
};

export type RuleFiredVia = 'trigger' | 'cancel';

export type RuleFiredRef = { ruleId: string; via: RuleFiredVia };

export type RuleConnectionDirection = 'up' | 'down';

export type RuleConnectionLink = { id: string; name: string; via: RuleFiredVia };

export type RuleConnectionFlow = Pick<GQL_RULE_SEARCHQuery['flows'][number], 'id' | 'name' | 'flow'>;

export type RuleConnectionViaDisplay = { icon: string } & Record<RuleConnectionDirection, string>;

export type RuleActionListUpdate = (actions: RuleFlowAction[], listId: string) => RuleFlowAction[];

export type RuleActionMove = { sourceUuid: string; targetListId: string; beforeUuid?: string | null };

export type RuleActionIfSlotMove = { sourceUuid: string; ifThenUuid: string };

export type RuleTriggerOption = {
  icon: FAIcon;
  name: string;
  desc: string;
  long: string;
  trigger: FlowTrigger;
};
